import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi, useAuth } from 'hooks';

type Payload = {
  userId: string;
  file: File;
  // When set, the result is attached to this existing lab (our own `Lab` row
  // id). When omitted, a new standalone result is created (prior labwork with
  // no Healthie order).
  labId?: string;
  // ISO yyyy-mm-dd. Only sent for a new result; an existing order keeps its own
  // date.
  labResultDateCreated?: string;
};

const useUploadLabResult = () => {
  const queryClient = useQueryClient();
  const { api } = useApi({ throwIfError: true });
  const { userToken } = useAuth();

  const mutation = useMutation({
    mutationFn: async ({
      userId,
      file,
      labId,
      labResultDateCreated,
    }: Payload) => {
      const formData = new FormData();
      formData.append('file', file);
      if (labId !== undefined) {
        formData.append('lab_id', labId);
      }
      if (labResultDateCreated) {
        formData.append('lab_result_date_created', labResultDateCreated);
      }

      return await api<FormData>({
        route: `/private/dashboard/user/${userId}/lab-result`,
        method: 'POST',
        token: userToken,
        payload: formData,
        // Omit the JSON content type so axios sets the multipart boundary.
        headers: { Accept: 'application/json' },
      });
    },
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({
        queryKey: ['hc-user-labs', userToken, userId],
      });
    },
  });

  return {
    mutation,
  };
};

export default useUploadLabResult;
